import { reportClientError } from '../lib/errorReporter.ts';
import {
  apiOrigin,
  apiUrl,
  authHeaders,
  clearCredentials,
  credentialsMode,
  isNative,
  mediaQuery,
  setSessionToken,
} from './origin.ts';

/** Mirrors RecordingTunables in server/src/settings.ts. */
export interface RecordingQualityShape {
  audioKbPerMinute: number;
  /**
   * How far the microphone is turned up before encoding, as a multiple. The one
   * recording setting about loudness rather than size — see amplify in
   * client/src/lib/capture.ts for why the browser is not doing it for us.
   */
  audioGain: number;
  /** The side of a кружочек's square frame, in pixels. */
  videoPx: number;
  videoFps: number;
  videoKbps: number;
  /**
   * Whether a кружочек may change camera at all. The one setting here that is a
   * switch rather than a number: with it off there is no flip button anywhere
   * and every кружочек is the front camera. See canFlipCamera in
   * client/src/lib/capture.ts, and videoJoin.ts for what a flip costs on the
   * web — inside the iOS app it costs nothing, because nothing is cut.
   */
  videoFlip: boolean;
}

/** Mirrors VideoImportTunables in server/src/settings.ts. */
export interface VideoImportShape {
  /** The long side of the re-encoded frame; the short side follows the aspect. */
  importPx: number;
  importFps: number;
  importKbps: number;
  importAudioKbps: number;
}

export interface Me {
  id: string;
  /** The credential — «@ник», what somebody types to start a chat with you. */
  username: string;
  /** The name everyone else reads. Falls back to the username server-side. */
  displayName: string;
  avatarPhotoId: string | null;
  isAdmin: boolean;
  /**
   * The recording settings this device has to apply itself, because the encoding
   * happens here. Delivered with the identity because that is the first thing
   * fetched, which means it is in place before anyone can hold the record button.
   */
  recording: RecordingQualityShape;
  /**
   * What a video picked from the gallery is re-encoded to before it goes up.
   * Here for the same reason as `recording` and a stronger one: the transcode
   * runs on this device, so these numbers decide what crosses the network at
   * all. See client/src/lib/videoCompress.ts.
   */
  videoImport: VideoImportShape;
  /**
   * Whether this deployment has a speech engine behind it. Not a setting the
   * client applies but a capability it has to know about: with no engine there
   * is no «Аа» button on a voice bubble, because pressing it could only ever
   * come back 503.
   */
  transcription: { enabled: boolean };
  /**
   * The largest one attachment may be. Enforced by the server whatever this
   * client believes; it is here so the compose screen can refuse an over-sized
   * video up front rather than letting the sender watch the rest of an album
   * upload behind one clip that was never going to be accepted.
   */
  maxUploadBytes: number;
  /**
   * True while the password on this account is one somebody else handed out —
   * an admin on «Пользователи», or the seed script. It is what puts «Придумайте
   * пароль» in front of everything else, so it travels with the identity rather
   * than only with the login: a phone reopened on that screen a day later starts
   * from /auth/me and nothing else.
   */
  mustChangePassword: boolean;
  /**
   * When the password that is on the account now was written, whoever wrote it.
   * `null` on an account whose password predates the column, which the settings
   * row reads as «nothing to claim» rather than as a date to invent.
   */
  passwordChangedAt: number | null;
}

/** Mirrors MessageKind in server/src/db.ts. */
export type MessageKind = 'text' | 'image' | 'file' | 'videoNote' | 'voice' | 'call' | 'album';

/** Mirrors AttachmentKind in server/src/db.ts. */
export type AttachmentKind = 'photo' | 'video';

/**
 * One tile of an album. Mirrors AttachmentView in server/src/chats.ts.
 *
 * `index` is both the sender's order and the address of the bytes — it is what
 * `fileUrl` puts in `?index=`.
 */
export interface AttachmentView {
  index: number;
  kind: AttachmentKind;
  fileName: string;
  fileSize: number;
  mime: string;
  /** Videos only: how long the clip runs. `null` on a photo. */
  durationMs: number | null;
  /**
   * Whether there is a still picture to draw in the tile at all. Always true of
   * a photo; true of a video only when the sender's browser managed to grab a
   * frame, and false is what stops the tile pointing an `<img>` at an MP4.
   */
  hasPreview: boolean;
}

/** Which shelf of the profile is being read. Mirrors MediaTab on the server. */
export type MediaTab = 'media' | 'notes' | 'voice';

/**
 * One tile in the profile's grid. Mirrors MediaItemView in server/src/chats.ts.
 *
 * `index` is the item's position inside an album, or `null` when the message is
 * one file — which is exactly what `fileUrl` wants, so a caller can hand it
 * straight over without asking which of the two it has.
 */
export interface MediaItem {
  messageId: string;
  index: number | null;
  /** A кружочек arrives as 'video'; the shelf it came from is what makes it round. */
  kind: AttachmentKind | 'voice';
  fileName: string;
  fileSize: number;
  mime: string;
  createdAt: number;
  senderId: string;
  durationMs: number | null;
  hasPreview: boolean;
}

export interface MediaPage {
  items: MediaItem[];
  hasMore: boolean;
  /** The whole shelf, not this page — the viewer's «58 из 60» needs the 60. */
  total: number;
}

/** Mirrors CallResult in server/src/db.ts. Only 'ended' carries a duration. */
export type CallResult = 'ended' | 'missed' | 'declined' | 'failed';

/** Mirrors the response of GET /api/calls/ice in server/src/routes/calls.ts. */
export interface IceConfig {
  iceServers: RTCIceServer[];
  /**
   * False when no TURN server is deployed. Calls then connect only between
   * networks that can reach each other directly — fine on one home Wi-Fi, often
   * not over mobile data. The UI uses this to explain a failure rather than
   * leaving it mysterious.
   */
  hasRelay: boolean;
  ringSeconds: number;
  /**
   * What this call may cost on the wire, set on the admin page. Optional
   * because a server older than the panel does not send it, and a client that
   * cannot read it falls back to the numbers it was built with — see
   * callTuning in lib/webrtc.ts.
   */
  tunables?: CallTuning;
}

/** What the encoder gives up first. Mirrors CALL_DEGRADATIONS on the server. */
export type CallDegradation = 'balanced' | 'maintain-framerate' | 'maintain-resolution';

/** Which video codec is offered first. Mirrors CALL_CODECS on the server. */
export type CallCodec = 'auto' | 'H264' | 'VP8' | 'VP9' | 'AV1';

/**
 * What a call may cost on the wire. Mirrors CallTunables in server/src/settings.ts.
 *
 * Delivered with the ICE credentials rather than with the session, so a change
 * on the admin page applies to the next call on every device — see /api/calls/ice.
 */
export interface CallTuning {
  callWidth: number;
  callHeight: number;
  callFps: number;
  callVideoKbps: number;
  callAudioKbps: number;
  callDegradation: CallDegradation;
  callCodec: CallCodec;
  /** Ask Opus for in-band forward error correction. */
  callAudioFec: boolean;
  /** The caller forbids its direct path first and lifts the ban if the relay gives nothing — a diagnostic, off by default. */
  callRelayFirst: boolean;
  callStatsSeconds: number;
}

/**
 * The calls panel: everything a call is set to, plus what it works out to.
 *
 * One knob more than CallTuning — how long the samples are kept is a fact about
 * this server's disk, and no browser has any use for it.
 */
export interface CallSettings extends CallTuning {
  callSamplesDays: number;
  /** A minute of call in МБ, one direction, as it costs the person. */
  mbPerMinute: number;
  /** What one relayed call costs the relay, in Mbit/s — both directions, twice. */
  relayMbitps: number;
  envDefaults: CallTuning;
  overridden: string[];
}

/**
 * One batch of what a device measured about a call. Mirrors TelemetryBatch in
 * lib/callTelemetry.ts, which is where its shape is decided — this is only the
 * type the request is made with.
 */
export interface CallTelemetryBatch {
  samples: unknown[];
  events: unknown[];
  final?: boolean;
}


/** One row of the calls list. Mirrors CallListEntry in server/src/call-telemetry.ts. */
export interface AdminCallRow {
  id: string;
  startedAt: number;
  answeredAt: number | null;
  endedAt: number | null;
  video: boolean;
  calleeVideo: boolean | null;
  callerName: string;
  calleeName: string;
  /**
   * 'page' | 'native' | null — which peer implementation each side ran. Not
   * which app they were in: the Android shell is on the page's peer, so this
   * says `page` for it too. `callerShell` is the one that answers that.
   */
  callerVia: string | null;
  calleeVia: string | null;
  /** 'web' | 'android' | 'ios' | null — which client each side was using. */
  callerShell: string | null;
  calleeShell: string | null;
  result: string | null;
  endReason: string | null;
  durationMs: number | null;
  videoOutBpsAvg: number | null;
  videoOutBpsMin: number | null;
  rttMsAvg: number | null;
  lossAvg: number | null;
  freezesMax: number | null;
  limitedShareMax: number | null;
  relayed: boolean | null;
  /** Diagnostic notes filed against this call — the «worth opening» signal. */
  notes: number;
}

/** One reading, as the chart reads it. Mirrors CallSampleView on the server. */
export interface AdminCallSample {
  at: number;
  audioOutBps: number | null;
  audioInBps: number | null;
  videoOutBps: number | null;
  videoInBps: number | null;
  /** RMS loudness of each direction over the sample's interval, 0..1. */
  audioOutLevel: number | null;
  audioInLevel: number | null;
  fpsOut: number | null;
  fpsIn: number | null;
  widthOut: number | null;
  heightOut: number | null;
  widthIn: number | null;
  heightIn: number | null;
  rttMs: number | null;
  jitterMs: number | null;
  loss: number | null;
  availableOutBps: number | null;
  limitation: string | null;
  limitedShare: number | null;
  freezes: number | null;
  route: string | null;
  relayed: boolean | null;
  codecOut: string | null;
  codecIn: string | null;
  /** A small JSON blob: encoder, decoder, nack/pli. Null on most samples. */
  extra: string | null;
}

export interface AdminCallSide {
  userId: string;
  name: string;
  role: 'caller' | 'callee';
  /** Which peer carried the media; `shell` is which app they were in. */
  via: string | null;
  shell: string | null;
  agent: string | null;
  samples: AdminCallSample[];
  truncated: boolean;
}

export interface AdminCallEvent {
  at: number;
  userId: string;
  name: string;
  kind: string;
  detail: string;
}

export interface AdminCallDetail {
  call: AdminCallRow;
  sides: AdminCallSide[];
  events: AdminCallEvent[];
}

export interface AdminCallSummary {
  days: number;
  calls: number;
  answered: number;
  relayed: number;
  medianVideoBps: number | null;
  medianRttMs: number | null;
  limited: number;
}

/** `null` clears an override and hands the value back to .env. */
export interface CallSettingsPatch {
  callWidth?: number | null;
  callHeight?: number | null;
  callFps?: number | null;
  callVideoKbps?: number | null;
  callAudioKbps?: number | null;
  callDegradation?: CallDegradation | null;
  callCodec?: CallCodec | null;
  callAudioFec?: boolean | null;
  callRelayFirst?: boolean | null;
  callStatsSeconds?: number | null;
  callSamplesDays?: number | null;
}

/** Mirrors ReactionView in server/src/chats.ts. */
export interface ReactionView {
  slug: string;
  count: number;
  /** Whether this viewer is one of the people who chose it. */
  mine: boolean;
}

/** Mirrors ReplyView in server/src/chats.ts: the message a reply quotes. */
export interface ReplyView {
  id: string;
  senderName: string;
  kind: MessageKind;
  /**
   * A text message's own body, smiley codes and all — so the quote renders the
   * way the bubble does. Any other kind carries the label instead.
   */
  preview: string;
  /**
   * The fragment this reply was written against, when it was written against
   * one rather than the whole message, and null otherwise. `preview` is already
   * built from it; this is here so an edit arriving over the socket can ask the
   * same question the server asks on a reload — is that fragment still in the
   * message — without going back for the row.
   */
  quote: string | null;
  /** The original is gone for everyone; the quote says so rather than vanishing. */
  deleted: boolean;
}

/**
 * Who wrote a forwarded message. Mirrors ForwardView in server/src/chats.ts.
 *
 * A name and nothing else, because that is all the server will say: the message
 * this one copies sits in a conversation the reader is not in, and an id would
 * be a way of asking about it.
 */
export interface ForwardView {
  name: string;
}

/**
 * What a reply points at: a message, and the part of it being answered.
 *
 * `quote` is a fragment somebody selected inside the original — it travels with
 * the reply because it is what they meant, and the original can be edited out
 * from under it afterwards.
 */
export interface ReplyTo {
  id: string;
  quote: string | null;
}

/** Mirrors TranscriptStatus in server/src/db.ts. */
export type TranscriptStatus = 'pending' | 'done' | 'failed';

/**
 * A voice message as text. Mirrors TranscriptView in server/src/chats.ts.
 *
 * `null` on a message means nobody has asked for one yet — which is not the same
 * as a `done` with no `text`, the answer for a recording with no words in it.
 */
export interface TranscriptView {
  status: TranscriptStatus;
  text: string | null;
  at: number | null;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  fromMe: boolean;
  /** Who wrote it — what a group thread labels its incoming bubbles with. */
  senderId: string;
  senderName: string;
  /** Their avatar, for the gutter beside an incoming bubble in a group. */
  senderAvatarId: string | null;
  kind: MessageKind;
  body: string | null;
  fileName: string | null;
  fileSize: number | null;
  mime: string | null;
  createdAt: number;
  readAt: number | null;
  /** Set once the sender rewrote it; the bubble then says «· изменено». */
  editedAt: number | null;
  /** Recordings only: how long the take runs, and the range the sender kept. */
  durationMs: number | null;
  trimStartMs: number | null;
  trimEndMs: number | null;
  /** Video notes only: when the recipient first played it. */
  viewedAt: number | null;
  /**
   * Calls only: how it ended, and whether a camera was involved. `senderId` is
   * whoever placed it, so `fromMe` is what makes the bubble read «исходящий».
   */
  callResult: CallResult | null;
  callVideo: boolean | null;
  reactions: ReactionView[];
  /** What this message answers, drawn as a quote above it. `null` when nothing. */
  replyTo: ReplyView | null;
  /**
   * Who wrote it, when it was forwarded here from somewhere else. `null` on
   * everything written where it sits, which is nearly every message.
   */
  forwardedFrom: ForwardView | null;
  /** Voice messages only: what it says, once anyone has had it recognised. */
  transcript: TranscriptView | null;
  /** An album's photos and videos, in the sender's order. Empty on every other kind. */
  attachments: AttachmentView[];
  /** Present only on the response to the sender's own POST. */
  clientId?: string;
  /**
   * One of the assistant's own lines that draws as something other than a
   * bubble — the memory divider, or a refusal with an action beside it. `null`
   * on every message in every chat but the assistant's.
   */
  aiNotice: AiNotice | null;
  /**
   * A picture the assistant made. `null` on everything else, uploads included —
   * a version is a position in a chain of attempts, and an upload starts one
   * rather than being one.
   */
  aiImage: AiImageView | null;
}

/** Mirrors AiImageView in server/src/ai-images.ts. */
export interface AiImageView {
  version: number;
  size: string;
  quality: string;
  edited: boolean;
  sourceMessageId: string | null;
  prompt: string;
}

/** Mirrors AiNotice in server/src/db.ts. */
export type AiNotice = 'reset' | 'quota' | 'failed' | 'redo';

/**
 * What the composer asked the assistant for, when it asked for anything.
 *
 * Sent with the message and stored beside it, so the answer is decided by what
 * the person chose rather than by a model guessing from the words. Absent is
 * «Авто», which is what every message sent from anywhere else means.
 */
/** Mirrors AiPrefs in server/src/ai-prefs.ts: what one person keeps set. */
export interface AiPrefs {
  /** Enabled shapes in display order — «1:1», «4:5», and any of their own. */
  ratios: string[];
  quality: 'low' | 'medium' | 'high';
}

export interface AiRequest {
  mode: 'auto' | 'chat' | 'image';
  /** «2:3» — an aspect, not a size; the server picks the nearest one it can make. */
  ratio?: string;
  quality?: 'low' | 'medium' | 'high';
  /**
   * «Повторить в качестве»: these words were re-sent by a button, not typed.
   * The server stamps the message so every device can label the bubble —
   * an unlabelled copy of one's own message reads as the chat glitching.
   */
  redo?: boolean;
}

/** Mirrors MessagePage in server/src/chats.ts: one page of a thread. */
export interface MessagePage {
  /** Oldest-first, ready to render. */
  messages: MessageDto[];
  /** Whether anything older than the first of them is still on the server. */
  hasMore: boolean;
  /**
   * Whether anything newer than the last of them is. Only a window — the page
   * asked for by `around`, which is how a search hit from last winter is
   * opened — can say yes; every other page ends where the conversation does.
   */
  hasNewer: boolean;
}

/** Mirrors MessageSearchPage in server/src/chats.ts: one page of matches. */
export interface MessageSearchPage {
  /** Newest first, which is the order the search screen lists them in. */
  messages: MessageDto[];
  /** Whether anything older than the last of them also matches. */
  hasMore: boolean;
  /** Matches in the whole conversation — the «из 4» in the strip. */
  total: number;
}

/**
 * Which past tense a sentence about somebody uses, mirroring `Gender` in
 * server/src/db.ts. Grammar rather than a statement about a person: it picks an
 * ending, and `presenceLabel` is the only thing that reads it.
 */
export type Gender = 'f' | 'm';

/** «У всех» is the sender's to use; «у меня» hides it on this side only. */
export type DeleteScope = 'all' | 'me';

/** Mirrors ConversationKind in server/src/db.ts. */
export type ChatKind = 'direct' | 'group' | 'notes';

export interface ChatSummary {
  id: string;
  kind: ChatKind;
  /**
   * What the row is called: the peer's name, the group's name, or what the owner
   * of a «Заметки» chat has renamed it to — «Заметки» until they do.
   */
  title: string;
  /**
   * The other person — `null` in a group, which has no single other person, and
   * in «Заметки», which has no other person at all.
   */
  peer: {
    id: string;
    /** Their nickname, which the header shows under the name they chose. */
    username: string;
    displayName: string;
    avatarPhotoId: string | null;
    /** The AI bot's chat, the one place typing «/» offers its commands. */
    assistant: boolean;
    /**
     * Whose API answers, on an assistant's chat; `null` on everybody else's.
     * The two bots do not offer the same things, and a composer showing
     * controls the bot ignores would be lying about what it can do.
     */
    assistantProvider: 'gemini' | 'openai' | null;
    /**
     * Which ending the line under their name takes. `null` — nobody was asked —
     * is the «был(а)» this app said about everybody before the column existed.
     */
    gender: Gender | null;
  } | null;
  /**
   * Set when the other side is a bot, carrying the folder an admin put it in.
   * `null` on a person's chat and on every group — a group with a bot in it is
   * still a room full of people, not the bot's own row.
   *
   * What the list needs and `peer.assistant` cannot answer: that flag is true
   * of the two AI accounts only, and this decides which side of the «БОТЫ»
   * divider a row belongs on.
   */
  bot: { group: string | null } | null;
  /**
   * The chat's own picture — a group's, or the one somebody has put on their
   * «Заметки». `null` on a direct chat, whose tile is the peer's avatar above,
   * and until anyone sets one, where the tile draws a glyph instead.
   */
  avatarPhotoId: string | null;
  online: boolean;
  /** When the peer was last connected, for the «был(а) …» line. */
  lastSeenAt: number | null;
  /** How many people are in the group; `null` on a direct chat. */
  memberCount: number | null;
  lastMessage: {
    /** Which message it is, so a repeat of it can be told from a new one. */
    id: string;
    kind: MessageKind;
    preview: string;
    createdAt: number;
    fromMe: boolean;
    /** Who wrote it, for a group snippet. `null` on a direct chat or one's own. */
    senderName: string | null;
    /** Calls only: which glyph the snippet draws. `null` on every other kind. */
    callVideo: boolean | null;
  } | null;
  /** Unread here, for this viewer; 0 when the row draws no badge. */
  unreadCount: number;
  /**
   * When this viewer pinned the chat to the top of their list, `null` when they
   * have not. A moment rather than a flag: the last one pinned stands first, so
   * the same field answers «закреплено ли» and «в каком порядке».
   */
  pinnedAt: number | null;
}

/** One line of the «Участники» list. Mirrors MemberView in server/src/chats.ts. */
export interface GroupMember {
  id: string;
  displayName: string;
  avatarPhotoId: string | null;
  isCreator: boolean;
}

/** A chat with its roster — what «Информация о группе» is drawn from. */
export interface ChatDetail extends ChatSummary {
  members: GroupMember[];
  /** Whether this viewer created it, which is what unlocks removing others. */
  amCreator: boolean;
}

/** An account, as the «Новая группа» picker lists them. */
export interface UserSummary {
  id: string;
  /** Kept beside the display name: it is what «Начать чат» matches on. */
  username: string;
  displayName: string;
  avatarPhotoId: string | null;
  /**
   * An integration's account rather than a person's. Nobody is signed in as it
   * and it never answers — what it does is deliver, into every chat it is in.
   */
  bot: boolean;
}

/** One photograph on a profile. Mirrors photoView in server/src/routes/profile.ts. */
export interface ProfilePhoto {
  id: string;
  fileName: string | null;
  fileSize: number;
  createdAt: number;
}

export interface PushConfig {
  /** Web Push is configured — the VAPID pair is present and usable. */
  enabled: boolean;
  publicKey: string | null;
  /**
   * APNs is configured, which is a separate question with a separate answer:
   * the app inside the native shell has no service worker to hold a Web Push
   * subscription and registers a device token instead. A client only ever uses
   * one of these and must not be discouraged by the other one's absence.
   */
  apns: boolean;
  /**
   * And the same again for Android, independent of both: a deployment may have
   * an Apple key and no Firebase project, or the reverse. The key names here
   * match `NativeTransport` below, so the check is a lookup rather than a
   * second branch that could disagree with the first.
   *
   * Optional because a server older than this bundle does not send it, and a
   * missing flag is «not configured» rather than a reason to try anyway.
   */
  fcm?: boolean;
}

/** Which registry a native token was minted by. See `subscribeNative`. */
export type NativeTransport = 'apns' | 'fcm';

export interface PushSettings {
  /** False hides the message text in the notification, showing only who wrote. */
  showPreview: boolean;
}

/** Mirrors PushDebugState / PushUserState in server/src/push.ts. */
export interface PushUserState {
  username: string;
  online: boolean;
  /** Somebody is in the app; only `watching` decides whether that silences anything. */
  foregroundClaimAges: number[];
  /** Which conversation each of those claims has open, `null` for none. */
  watching: (string | null)[];
  subscriptions: { host: string; lastAckSecondsAgo: number | null }[];
  /** Over the last hour. `acked < sent` means pushes are not reaching the device. */
  delivery: {
    /** Handed to a push service; `acked` is a ratio of these. */
    sent: number;
    /** Refused by the push service, so never handed over — a different fault. */
    failed: number;
    /** The most recent refusal, status and all. */
    lastError: string | null;
    acked: number;
    medianLatency: number | null;
    worstLatency: number | null;
  };
}

export interface PushDecisionLog {
  at: number;
  username: string;
  category: string;
  outcome: string;
  detail: string;
}

export interface PushTuning {
  configured: boolean;
  serverTime: string;
  users: PushUserState[];
  recent: PushDecisionLog[];
}

/** How uploads are re-encoded. Mirrors ImageTunables in server/src/settings.ts. */
export type ImageFormat = 'webp' | 'jpeg' | 'original';

export interface ImageSettingsShape {
  quality: number;
  maxWidth: number;
  maxHeight: number;
  format: ImageFormat;
  /** Applies to every attachment, not only images. */
  maxUploadMb: number;
}

export interface ImageTuning extends ImageSettingsShape {
  envDefaults: ImageSettingsShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface ImageSettingsPatch {
  quality?: number | null;
  maxWidth?: number | null;
  maxHeight?: number | null;
  format?: ImageFormat | null;
  maxUploadMb?: number | null;
}

/** Mirrors RecordingTunables in server/src/settings.ts, plus the derived figures. */
export interface RecordingTuning extends RecordingQualityShape {
  /** What the audio allowance works out to as a codec setting; server-derived. */
  audioKbps: number;
  /**
   * What a minute of кружочек weighs at these settings — video plus audio, and
   * also the largest one that can exist, since a кружочек is capped at 60s.
   */
  videoMbPerMinute: number;
  envDefaults: RecordingQualityShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface RecordingSettingsPatch {
  audioKbPerMinute?: number | null;
  audioGain?: number | null;
  videoPx?: number | null;
  videoFps?: number | null;
  videoKbps?: number | null;
  videoFlip?: boolean | null;
}

/** Mirrors VideoImportTunables in server/src/settings.ts, plus the derived figure. */
export interface VideoImportTuning extends VideoImportShape {
  /** A minute of re-encoded video at these settings, picture and sound together. */
  mbPerMinute: number;
  envDefaults: VideoImportShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface VideoImportSettingsPatch {
  importPx?: number | null;
  importFps?: number | null;
  importKbps?: number | null;
  importAudioKbps?: number | null;
}

/** Mirrors TranscriptionTunables in server/src/settings.ts, plus what .env decided. */
export interface TranscriptionSettingsShape {
  /** Recognise every voice message as it arrives, rather than on request. */
  transcribeAuto: boolean;
  /** The longest recording worth queueing, in seconds. */
  transcribeMaxSeconds: number;
}

export interface TranscriptionTuning extends TranscriptionSettingsShape {
  /**
   * Whether an engine is configured at all. With none, both switches below are
   * settings for something that will not happen — which the panel says rather
   * than leaving somebody to wonder why nothing was recognised.
   */
  configured: boolean;
  /** `command` (a local binary) or `http` (an endpoint); empty when unconfigured. */
  provider: string;
  envDefaults: TranscriptionSettingsShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface TranscriptionSettingsPatch {
  transcribeAuto?: boolean | null;
  transcribeMaxSeconds?: number | null;
}

/** Mirrors AiTunables in server/src/settings.ts. */
export interface AiSettingsShape {
  /** The kill switch: off, the bot exists but stays silent. */
  aiReplies: boolean;
  /** How many messages of the thread travel with a chat request. */
  aiHistoryMessages: number;
  /** Mirrors of Gemini's free-tier requests-per-day, per model kind. */
  aiTextRpd: number;
  aiImageRpd: number;
  /** One person's share of a day, and the deployment's of a month. */
  aiUserTextPerDay: number;
  aiUserImagePerDay: number;
  aiMonthlyBudgetCents: number;
  /** What a picture is when the composer did not say. */
  aiImageSize: string;
  aiImageQuality: string;
}

/** One assistant. Mirrors AiProviderState in server/src/routes/admin.ts. */
export interface AiProviderTuning {
  id: 'gemini' | 'openai';
  configured: boolean;
  textModel: string;
  imageModel: string;
  botUsername: string;
}

/** The month, split two ways. Mirrors SpendSummary in server/src/ai-budget.ts. */
export interface AiSpend {
  monthMicros: number;
  budgetMicros: number;
  byProvider: { provider: string; kind: string; calls: number; micros: number }[];
  byUser: { userId: string; name: string; calls: number; micros: number }[];
}

export interface AiTuning extends AiSettingsShape {
  /** Whether there is a key at all — for either bot. Without one nothing answers. */
  configured: boolean;
  /** One row per assistant; the keys themselves never travel. */
  providers: AiProviderTuning[];
  spend: AiSpend;
  /** The persona, as currently in force — override or .env default. */
  systemPrompt: string;
  systemPromptOverridden: boolean;
  /** Today's spend by the server's own counter — an estimate, not Google's. */
  usedToday: { text: number; image: number };
  envDefaults: AiSettingsShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface AiSettingsPatch {
  aiReplies?: boolean | null;
  aiHistoryMessages?: number | null;
  aiTextRpd?: number | null;
  aiUserTextPerDay?: number | null;
  aiUserImagePerDay?: number | null;
  aiMonthlyBudgetCents?: number | null;
  aiImageSize?: string | null;
  aiImageQuality?: string | null;
  aiImageRpd?: number | null;
  systemPrompt?: string | null;
}

/** Mirrors GalleryPreviewTunables in server/src/settings.ts. */
export interface GalleryPreviewSettingsShape {
  /** One photograph's middle-tier preview, in КБ. */
  previewPhotoKb: number;
  /** One minute of video preview, in КБ. */
  previewVideoKbPerMinute: number;
  /** What a phone may pull out of iCloud per day to make previews, in МБ. 0 — не качать. */
  previewCloudMbPerDay: number;
  /** «Не раньше чем» под заявкой на короткое фоновое окно при недосланном, в минутах. */
  windowBacklogRefreshMin: number;
  /** То же для длинного окна при недосланном, в минутах. */
  windowBacklogProcessingMin: number;
  /** Пол для обоих окон, когда всё отправлено, в минутах. */
  windowIdleMin: number;
  /** Сводки в чат от сервера («Сводка: Т — окно 15:21, +3ф»). */
  digestEnabled: boolean;
  /** Тихие часы сводок, [from, to) по времени сервера. */
  digestQuietFromHour: number;
  digestQuietToHour: number;
}

export interface GalleryPreviewTuning extends GalleryPreviewSettingsShape {
  /** An hour of video preview at the current target, in МБ — the derived figure. */
  videoMbPerHour: number;
  envDefaults: GalleryPreviewSettingsShape;
  /** Keys currently overridden from the admin page rather than coming from .env. */
  overridden: string[];
}

/** `null` clears an override and hands the value back to .env. */
export interface GalleryPreviewSettingsPatch {
  previewPhotoKb?: number | null;
  previewVideoKbPerMinute?: number | null;
  previewCloudMbPerDay?: number | null;
  windowBacklogRefreshMin?: number | null;
  windowBacklogProcessingMin?: number | null;
  windowIdleMin?: number | null;
  digestEnabled?: boolean | null;
  digestQuietFromHour?: number | null;
  digestQuietToHour?: number | null;
}

/** One tuned knob, as it would be written in .env. */
export interface EnvOverride {
  name: string;
  value: string;
  /** What .env says today — what this line would be replacing. */
  envValue: string;
}

export interface EnvOverrides {
  overrides: EnvOverride[];
  /** The same thing as pasteable lines, which is the point of the whole feature. */
  text: string;
}

/** An account as the admin page lists it. Mirrors AccountView in server/src/accounts.ts. */
export interface AdminAccount {
  id: string;
  username: string;
  isAdmin: boolean;
  createdAt: string;
  /** Last moment they held a socket; `null` for someone who never has. */
  lastSeenAt: number | null;
  /** Live sessions — one per signed-in browser. */
  sessions: number;
  /**
   * The integration this account posts for, or null for a person. A bot has no
   * sessions and a password nobody holds, so without this it reads in the table
   * as an account somebody made and abandoned. Added by the admin route rather
   * than by AccountView — see the comment there for why accounts.ts stays out
   * of it.
   */
  bot: string | null;
}

/** A new account, as the «Пользователи» form asks for one. */
export interface NewAccount {
  username: string;
  password: string;
  isAdmin: boolean;
  /**
   * Take over a nickname that already exists — resetting that person's password
   * and role, and revoking their sessions. Off on the first attempt, so a
   * nickname that collides comes back as a 409 to be confirmed rather than
   * silently locking someone out of their own account.
   */
  overwrite?: boolean;
}

/**
 * What devices reported they could not reach. Mirrors ClientErrorSummary in
 * server/src/client-errors.ts.
 *
 * Totals only, by design: what actually failed lives in the `client_errors`
 * table and is meant to be read with a SQL client rather than on a page that
 * refreshes every three seconds.
 */
/**
 * A report somebody sent on purpose. Mirrors ReportView in server/src/reports.ts.
 *
 * `context` is assembled by the server, because an admin reads it without being
 * able to open the conversation it came from. `note` is the only part written
 * by a person, and is usually absent — the button is one tap and the plate it
 * sits on already says what went wrong.
 */
export interface AdminReport {
  id: string;
  kind: string;
  userName: string;
  note: string | null;
  context: string;
  createdAt: number;
}

export interface ClientErrorTotals {
  total: number;
  /** Of those, how many happened in the last 24 hours. */
  recent: number;
  /** Every kind seen across everyone, biggest first: [['api', 12], …]. */
  kinds: [string, number][];
  users: {
    username: string;
    total: number;
    recent: number;
    /** By the device's clock, which is the one that knows when it broke. */
    lastAt: number;
    kinds: [string, number][];
  }[];
}

/** What a walk of one directory found, as the server measured it. */
export interface AdminDiskUse {
  files: number;
  bytes: number;
}

/** One account's camera-roll backup, as the admin panel reads it. */
export interface AdminGalleryUser {
  userId: string;
  username: string;
  devices: number;
  /** Every photograph and video whose thumbnail is here, tombstones included. */
  assets: number;
  photos: number;
  videos: number;
  thumbBytes: number;
  /** Middle-tier previews fully here, and what they weigh. */
  previews: number;
  previewBytes: number;
  /** Originals somebody asked for, in total… */
  requested: number;
  /** …and still owed: asked for, not yet fully arrived. */
  pending: number;
  /** Originals fully here, whether or not their bytes have since been freed. */
  stored: number;
  originalBytes: number;
  /** Rows whose bytes were freed. They still answer «уже здесь» to the phone. */
  purged: number;
  deleted: number;
  /** The last line this person's phone posted to the sync journal. */
  lastSyncAt: number | null;
  lastSyncEvent: string | null;
  lastSyncText: string | null;
  /**
   * How many photographs the phones say they have, what they say has gone, and
   * how much of it they cannot read. The only figures here the server does not
   * work out for itself, and the denominator the rest of the row needs: 411
   * миниатюр is a finished backup or a quarter of one, and the phone is the
   * only one that knows which.
   *
   * Null where no phone has ever posted numbers — an app too old to count, not
   * a library with nothing in it.
   */
  onPhone: number | null;
  phoneDone: number | null;
  phoneSkipped: number | null;
  /** Measured on disk, not summed from the columns — the gap is the point. */
  disk: { thumbs: AdminDiskUse; originals: AdminDiskUse };
}

/** One line of the sync journal, as the log window shows it. */
export interface AdminGallerySyncLine {
  id: string;
  device_id: string;
  run_id: string;
  source: string;
  event: string;
  text: string;
  total: number | null;
  done: number | null;
  skipped: number | null;
  wanted: number | null;
  bytes: number | null;
  status: number | null;
  /** The phone's clock, which is the order things happened in. */
  occurred_at: number;
  /** This machine's. The gap is how long the line waited for a network. */
  reported_at: number;
}

export interface AdminGalleryStats {
  users: AdminGalleryUser[];
  totals: Omit<AdminGalleryUser, 'userId' | 'username' | 'lastSyncEvent' | 'lastSyncText'>;
  disk: {
    thumbs: AdminDiskUse;
    originals: AdminDiskUse;
    /** Files under no owner's directory — everything left of the old layout. */
    legacy: AdminDiskUse;
  };
  measuredAt: number;
}

/**
 * A tier and a kind — the six things the chart draws, in stacking and colour
 * order. Mirrors GALLERY_SERIES on the server, and the order is load-bearing on
 * both sides: a series keeps its colour when its neighbours fall to zero.
 */
export const GALLERY_SERIES = [
  'thumb.photo',
  'thumb.video',
  'preview.photo',
  'preview.video',
  'original.photo',
  'original.video',
] as const;

export type AdminGallerySeries = (typeof GALLERY_SERIES)[number];

/** The four windows the chart offers. The bucket comes back with the data. */
export type AdminGalleryRange = 'halfHour' | 'hour' | 'sixHours' | 'day';

/** One series in one bucket: how many arrived, and what they weighed. */
export interface AdminGalleryCell {
  count: number;
  bytes: number;
}

export interface AdminGalleryBucket {
  /** The bucket's first millisecond, aligned to this browser's day. */
  at: number;
  series: Record<AdminGallerySeries, AdminGalleryCell>;
}

/**
 * What arrived when — the chart, as opposed to the tiles' «сколько всего».
 *
 * The byte figures are what was received in the window rather than what is
 * still on disk: a purge in July does not un-upload June. The tiles above the
 * chart are the other question, and they are measured by walking the disk for
 * exactly that reason.
 */
export interface AdminGalleryTimeline {
  userId: string | null;
  range: AdminGalleryRange;
  bucketMs: number;
  /** Half-open: `from` starts the first bucket, `to` ends the last. */
  from: number;
  to: number;
  tzOffset: number;
  buckets: AdminGalleryBucket[];
  /** The window added up, summed from the buckets rather than queried again. */
  totals: Record<AdminGallerySeries, AdminGalleryCell>;
  measuredAt: number;
}

/** What one purge freed, and what it deliberately left alone. */
export interface AdminGalleryPurge {
  assets: number;
  thumbs: number;
  previews: number;
  originals: number;
  bytes: number;
  /** Originals still arriving: their partial file is a phone's resume offset. */
  skipped: number;
  /** Rows whose file had already gone. Marked anyway. */
  missing: number;
}

export interface AdminOverview {
  totals: {
    users: number;
    conversations: number;
    messages: number;
    textMessages: number;
    images: number;
    files: number;
    videoNotes: number;
    voiceMessages: number;
    /** Groups of photos and videos — messages, not tiles. */
    albums: number;
    /** Voice messages that have been read rather than only listened to. */
    transcripts: number;
    attachmentBytes: number;
    unread: number;
    sessions: number;
    disk: { files: number; bytes: number };
  };
  /** Every account there is — the app has no signup screen, so this is the roll. */
  accounts: AdminAccount[];
  push: PushTuning;
  clientErrors: ClientErrorTotals;
  /** Unresolved «Пожаловаться», newest first. Read one at a time, never counted. */
  reports: AdminReport[];
  images: ImageTuning;
  recording: RecordingTuning;
  videoImport: VideoImportTuning;
  transcription: TranscriptionTuning;
  ai: AiTuning;
}

/** A chat an integration's bot is in — which is a chat it delivers into. */
export interface IntegrationTarget {
  id: string;
  title: string;
}

/** Mirrors IntegrationView in server/src/integrations.ts. Never carries the token. */
export interface Integration {
  id: string;
  name: string;
  slug: string;
  botUserId: string;
  botUsername: string;
  /** All that is kept of the token once it has been shown; see db.ts. */
  tokenPrefix: string;
  createdAt: number;
  /** When a request last arrived. `null` means it never has. */
  lastUsedAt: number | null;
  disabled: boolean;
  /** Whether the token also opens the read-only reports at /api/service. */
  serviceRead: boolean;
  /** Everywhere it delivers: the chats its bot has been added to. */
  targets: IntegrationTarget[];
  /** Enquiries delivered, counted once each however many chats got them. */
  messages: number;
}

/**
 * Who a bot is for. Mirrors BotAccessMode in server/src/db.ts.
 *
 * 'admins' is what a bot nobody has decided about is: the design's «Только
 * админ», and the state a newly made bot is in.
 */
export type BotAccessMode = 'admins' | 'selected' | 'everyone';

/** One row of the «Боты» tab. Mirrors BotView in server/src/bot-access.ts. */
export interface AdminBot {
  botUserId: string;
  username: string;
  name: string;
  /** An outside service's bot, or one of the assistants. */
  kind: 'integration' | 'ai';
  mode: BotAccessMode;
  /** The folder it sits in on the chat list, or `null` for a row of its own. */
  group: string | null;
  /** Who it is open to — the list «Выбранные» edits, empty in the other modes. */
  users: { id: string; username: string; displayName: string }[];
}

/** A folder and how many bots wear its name; see the table comment in db.ts. */
export interface BotGroup {
  name: string;
  bots: number;
}

/** What every call on the «Боты» tab answers with: the page, whole. */
export interface BotsPayload {
  bots: AdminBot[];
  groups: BotGroup[];
}

/** A new integration, as the «Интеграции» form asks for one. */
export interface NewIntegration {
  name: string;
  /** Latin, digits and hyphens: it becomes the bot's nickname. */
  slug: string;
  /** Grant the read-only reports along with delivery; off by default. */
  serviceRead?: boolean;
}

/**
 * A recording on its way out. `kind` is declared rather than sniffed: only the
 * client knows whether the camera was pointed at anyone, and the server takes
 * it only for the two recorded kinds (see recordedKind in routes/chats.ts).
 */
export interface OutgoingMedia {
  kind: 'videoNote' | 'voice';
  blob: Blob;
  fileName: string;
  durationMs: number;
  /** The range the sender kept in the trim screen; null means the whole take. */
  trimStartMs: number | null;
  trimEndMs: number | null;
  /** A still frame; a кружочек shows it before anyone presses play. */
  poster: Blob | null;
}

/** One photo or video picked into an album, with what the sender's device could read off it. */
export interface OutgoingItem {
  file: File;
  kind: AttachmentKind;
  /**
   * A frame grabbed from the video, which becomes the tile's picture. `null` on
   * a photo — the server makes those itself — and on a video the browser refused
   * to decode, whose tile then draws a placeholder rather than a broken image.
   */
  poster: Blob | null;
  /** Videos only: how long the clip runs, read off the element that grabbed the frame. */
  durationMs: number | null;
  /**
   * The tile's picture while it is still on this device — an object URL for the
   * photo itself, or for a video's grabbed frame. `null` for a video the browser
   * would not decode, whose tile draws a placeholder: a video file handed to an
   * `<img>` is a broken image, and the one thing worse than no preview is one
   * that looks like a failure.
   */
  previewUrl: string | null;
}

/** A group of photos and videos on its way out, under one optional caption. */
export interface OutgoingAlbum {
  items: OutgoingItem[];
  caption: string;
}

/** A freshly opened upload session. Mirrors POST /api/uploads. */
export interface UploadTicket {
  id: string;
  offset: number;
  totalBytes: number;
}

/** What the server holds for a session — the answer a resuming send asks for. */
export interface UploadStatus {
  id: string;
  offset: number;
  totalBytes: number;
  complete: boolean;
  /** A message already owns these bytes; sending more of them is meaningless. */
  consumed: boolean;
}

/**
 * What became of one chunk. `ok: false` is the server correcting this device's
 * idea of where it had got to, not a failure — see api.sendChunk.
 */
export type ChunkResult =
  | { ok: true; offset: number; complete: boolean }
  | { ok: false; offset: number };

/**
 * A message built out of uploads that are already on the server.
 *
 * The field names are the ones the multipart path has always used, because the
 * route reads them from the same place either way — `posterFor` and
 * `mediaDurationMs` are positional lists here for exactly the reason they are
 * repeated fields there.
 */
export interface OutgoingMessage {
  clientId: string;
  replyToId: string | null;
  /** The fragment of the answered message this reply is against, if any. */
  replyQuote?: string | null;
  /** A recording declares which of the two it is; nothing else sets this. */
  kind?: 'videoNote' | 'voice';
  /** A text body, or an album's caption — the same column, as it always was. */
  body?: string;
  mime?: string;
  durationMs?: number;
  trimStartMs?: number;
  trimEndMs?: number;
  /** Per album item, positional. An empty string is "not a video". */
  mediaDurationMs?: string[];
  /** Which item each poster in `uploads.mediaPoster` belongs to. */
  posterFor?: number[];
  /** The finished sessions, by the field each one stands in for. */
  uploads?: {
    file?: string;
    poster?: string;
    media?: string[];
    mediaPoster?: string[];
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

/**
 * The two failures worth a note on the device: nothing answered at all, and the
 * server answered with a fault of its own.
 *
 * Everything else a 4xx says is an answer rather than a failure — «нет такого
 * ника» is a 404 and «занят» is a 409, and recording those would bury the two
 * cases this exists to make visible under the app working as designed. A 401 is
 * left out for the same reason: an expired session is ordinary.
 */
function worthReporting(status: number): boolean {
  return status === 0 || status >= 500;
}

/**
 * How long a request may go unanswered before it is treated as no answer at all.
 *
 * There was no deadline here, and what that costs is not a slow request but a
 * blank screen: a `fetch` that neither answers nor fails leaves whoever awaited
 * it in its loading state for ever. RequireAuth draws an empty surface while
 * /auth/me is in flight, and on a phone whose signal has dropped to nine
 * kilobytes a second that surface is the entire app, with nothing to press. A
 * rejection is a state something can be drawn from; a promise that never
 * settles is not — and it leaves nothing in client_errors either, so the one
 * failure nobody can see from here is also the one nobody can see from there.
 *
 * Twenty seconds because it has to sit well above the slowest honest round trip
 * — a chat list over a bad tunnel is seconds, not tens of them — and well below
 * how long a person will look at nothing before deciding the app is broken.
 */
const TIMEOUT_MS = 20_000;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // FormData must set its own Content-Type — the multipart boundary is in it.
  const isJsonBody = init.body !== undefined && !(init.body instanceof FormData);

  // A multipart body is a photograph, and how long it takes to go up is a fact
  // about its size and the network rather than a fault — the deadline that
  // protects a screen would cancel an upload that was going perfectly well.
  // These are the only bodies here that carry bytes; a message's attachment
  // goes through sendChunk, which has its own abort and its own reasons.
  const deadline = init.body instanceof FormData ? null : AbortSignal.timeout(TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      credentials: credentialsMode(),
      headers: {
        ...(isJsonBody ? { 'Content-Type': 'application/json' } : {}),
        // Empty on the web, where the cookie above is the credential.
        ...authHeaders(),
      },
      ...init,
      // After the spread deliberately: nothing calling this passes a signal of
      // its own, and the deadline is not something a caller may drop by
      // accident.
      ...(deadline ? { signal: deadline } : {}),
    });
  } catch (error) {
    // Offline, the server went away mid-request, or nothing came back inside
    // TIMEOUT_MS. Nothing on the other side saw any of it happen, which is
    // precisely why the device writes it down — a timeout arrives here named
    // `TimeoutError`, so the two are told apart in the table without a column.
    //
    // Except on a page that had already left the screen. Both WebViews cut
    // every request in flight when the app goes to the background, and those
    // land here looking exactly like a network that failed — three endpoints
    // rejecting in the same second, on an iPhone whose owner had put it in a
    // pocket. Judged by visibility rather than by the error's name, which is
    // the trap next door: WebKit reports a cut and an expired
    // AbortSignal.timeout with the same `AbortError`, so a rule written about
    // the name would also swallow the twenty-second wait somebody sat through.
    // A hidden page is the honest test of «nobody was there to see it», and the
    // cold start on a dead network — the failure this table exists for —
    // happens with the page in front of somebody and goes on being recorded.
    const unseen = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (!unseen) {
      reportClientError({
        kind: 'api',
        detail: path,
        message: error instanceof Error ? `${error.name}: ${error.message}` : 'fetch failed',
        status: 0,
      });
    }
    // Both are status 0 — «никто не ответил» — because every caller treats them
    // the same and a second code would only be a second thing to handle.
    throw new ApiError(
      error instanceof Error && error.name === 'TimeoutError'
        ? 'Сервер не ответил'
        : 'Нет соединения',
      0,
    );
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    if (worthReporting(res.status)) {
      reportClientError({
        kind: 'api',
        detail: path,
        message: body?.error ?? res.statusText ?? 'server error',
        status: res.status,
      });
    }
    throw new ApiError(body?.error ?? 'Что-то пошло не так', res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  /**
   * `wantToken` asks for the session id in the body, and only the native build
   * does: it is cross-origin to the server, so the cookie set alongside it will
   * never be sent back. The web client does not ask, and so never has a session
   * id anywhere script can reach.
   */
  login: async (username: string, password: string) => {
    const { token, ...me } = await request<Me & { token?: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, wantToken: isNative }),
    });
    if (isNative) setSessionToken(token ?? null);
    return me as Me;
  },

  logout: async () => {
    // Cleared whatever the server says. A logout that failed to reach it still
    // has to end the session on this device; the row is swept when it expires.
    try {
      return await request<{ ok: true }>('/auth/logout', { method: 'POST' });
    } finally {
      clearCredentials();
    }
  },

  me: () => request<Me>('/auth/me'),

  /**
   * Replacing your own password. `currentPassword` is null only on the screen
   * that meets somebody signing in with a password an admin handed them — the
   * server decides whether that is allowed, from a flag this client cannot set.
   *
   * Answers with the whole identity, `mustChangePassword` cleared, which is what
   * makes that screen go away without anything to navigate.
   *
   * A wrong current password comes back as 403 rather than another 400, so the
   * form can put it under the first field instead of at the bottom.
   */
  changePassword: (currentPassword: string | null, newPassword: string) =>
    request<Me>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  /** The credential `<img>` and `<video>` use; see mediaQuery in api/origin.ts. */
  mediaTicket: () => request<{ ticket: string; expiresAt: number }>('/auth/media-ticket'),

  listChats: () => request<ChatSummary[]>('/chats'),

  /** «Начать чат»: find-or-create by nickname. 404 means no such account. */
  startChat: (nickname: string) =>
    request<ChatSummary>('/chats', { method: 'POST', body: JSON.stringify({ nickname }) }),

  /** Everyone but yourself — the contact list the group picker is built from. */
  listUsers: () => request<UserSummary[]>('/users'),

  /**
   * This phone, and that its owner agreed to back the camera roll up here.
   *
   * The only gallery call this client makes: everything after it — the resume
   * handshake and the thumbnails themselves — is the native engine's, because
   * it has to work in a background task with no WebView running. See
   * client/src/lib/gallery.ts and docs/GALLERY.md.
   */
  registerGalleryDevice: (deviceId: string, consentVersion: string, platform: string) =>
    request<{ deviceId: string; storedCount: number }>('/gallery/device', {
      method: 'POST',
      body: JSON.stringify({ deviceId, consentVersion, platform }),
    }),

  /**
   * What the web half of the sync did, for `gallery_sync_log`.
   *
   * The second and last gallery call from here, and the only one that is not
   * about a photograph. The engine posts its own lines directly from Swift —
   * it has to, since the run that matters most happens with no WebView alive —
   * so this carries only what happens on this side of the bridge: which button
   * was pressed, whether the autostart even tried. See lib/gallerySyncReport.ts
   * and docs/GALLERY-DEBUG.md.
   */
  logGallerySync: (deviceId: string, entries: unknown[]) =>
    request<{ stored: number; max: number }>('/gallery/log', {
      method: 'POST',
      body: JSON.stringify({ deviceId, entries }),
    }),

  /**
   * ICE servers for a call, with a TURN credential that expires within the hour.
   *
   * Fetched per call rather than once at sign-in, precisely because it expires:
   * a tab left open overnight would otherwise place its first morning call with
   * a credential the relay has stopped accepting.
   */
  iceConfig: () => request<IceConfig>('/calls/ice'),

  /**
   * What this device measured about a call it is on.
   *
   * `keepalive` only on the last batch, and that asymmetry is deliberate: it is
   * what lets the closing readings survive a page being torn down, and Chrome
   * gives keepalive requests a shared 64 KB budget which an ordinary flush has
   * no business spending (see the comment in errorReporter.ts). A batch that
   * does not get through is a hole in a chart and nothing more, so nothing here
   * is retried harder than the next flush.
   */
  reportCallTelemetry: (callId: string, batch: CallTelemetryBatch) =>
    request<{ samples: number; events: number }>(`/calls/${callId}/telemetry`, {
      method: 'POST',
      body: JSON.stringify(batch),
      keepalive: batch.final === true,
    }),

  /** «Новая группа»: a name and the people picked before it. */
  createGroup: (title: string, memberIds: string[]) =>
    request<ChatSummary>('/chats/groups', {
      method: 'POST',
      body: JSON.stringify({ title, memberIds }),
    }),

  /** The chat plus its roster, for «Информация о группе». */
  chatDetail: (chatId: string) => request<ChatDetail>(`/chats/${chatId}`),

  /**
   * Your own id leaves the group; anyone else's removes them, which only the
   * creator may do. One call, because it is one thing happening to one row.
   */
  removeMember: (chatId: string, userId: string) =>
    request<void>(`/chats/${chatId}/members/${userId}`, { method: 'DELETE' }),

  /**
   * The picture on a chat that wears one of its own — a group or «Заметки» —
   * replaced. The creator's to change; 403 for anyone else. Everyone in the
   * chat, this device included, is told over the socket, so the list is left to
   * `conversation:updated` rather than patched here.
   */
  setChatAvatar: (chatId: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return request<{ avatarPhotoId: string }>(`/chats/${chatId}/avatar`, {
      method: 'PUT',
      body: form,
    });
  },

  /**
   * «Заметки», renamed. Only «Заметки» — a group's name is nobody's to change,
   * here or on the server. An empty string is how the default comes back rather
   * than an error: it stores nothing and answers «Заметки».
   */
  renameChat: (chatId: string, title: string) =>
    request<ChatSummary>(`/chats/${chatId}/title`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    }),

  /**
   * A chat kept at the top of the list, or let go of. Nobody else is told — the
   * pin is a fact about this list — so the answer is this viewer's summary and
   * the socket only reaches their own other devices.
   */
  pinChat: (chatId: string, pinned: boolean) =>
    request<ChatSummary>(`/chats/${chatId}/pin`, {
      method: 'PUT',
      body: JSON.stringify({ pinned }),
    }),

  /**
   * The newest page of a thread, or the page beside a message this device
   * names: `before` is the oldest message it holds, `after` the newest, and
   * `around` asks for a window centred on one — how the search screen opens a
   * hit without paging back to it. `limit` is for a reconnect asking back for
   * everything it had on screen; the server clamps it.
   */
  listMessages: (
    chatId: string,
    options: { before?: string; after?: string; around?: string; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (options.before !== undefined) query.set('before', options.before);
    if (options.after !== undefined) query.set('after', options.after);
    if (options.around !== undefined) query.set('around', options.around);
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    const suffix = query.toString();
    return request<MessagePage>(`/chats/${chatId}/messages${suffix ? `?${suffix}` : ''}`);
  },

  /**
   * «Поиск по сообщениям» in one conversation: the matches, newest first, and
   * how many there are in all. `before` is the last match already listed.
   *
   * A query the server considers too short comes back as an empty page rather
   * than an error — the field is in that state most of the time it is open.
   */
  searchMessages: (chatId: string, q: string, options: { before?: string; limit?: number } = {}) => {
    const query = new URLSearchParams({ q });
    if (options.before !== undefined) query.set('before', options.before);
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    return request<MessageSearchPage>(`/chats/${chatId}/messages/search?${query.toString()}`);
  },

  /**
   * The same over every conversation, for the field above the chat list. Each
   * match carries its `conversationId`, which is what the row needs to name the
   * chat it came from — the list is already loaded, so nothing else has to be.
   */
  searchAllMessages: (q: string, options: { before?: string; limit?: number } = {}) => {
    const query = new URLSearchParams({ q });
    if (options.before !== undefined) query.set('before', options.before);
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    return request<MessageSearchPage>(`/chats/search?${query.toString()}`);
  },

  /**
   * One shelf of a conversation's media for the profile's grid, newest first.
   *
   * The cursor is a pair, unlike the thread's: a page can end in the middle of an
   * album, and the message id alone would not say which of its items was the last
   * one seen. Pass both fields of the tile the grid stops at.
   */
  conversationMedia: (
    chatId: string,
    options: { tab?: MediaTab; before?: string; beforeIndex?: number | null; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (options.tab !== undefined) query.set('tab', options.tab);
    if (options.before !== undefined) query.set('before', options.before);
    if (options.beforeIndex !== undefined && options.beforeIndex !== null) {
      query.set('beforeIndex', String(options.beforeIndex));
    }
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    const suffix = query.toString();
    return request<MediaPage>(`/chats/${chatId}/media${suffix ? `?${suffix}` : ''}`);
  },

  /**
   * `replyToId` is the message being answered, on all three senders. The server
   * drops it rather than refusing the message if it no longer points at anything
   * — see replyTarget in server/src/chats.ts.
   */
  sendText: (
    chatId: string,
    body: string,
    clientId: string,
    replyTo: ReplyTo | null,
    ai?: AiRequest,
  ) =>
    request<MessageDto>(`/chats/${chatId}/messages`, {
      method: 'POST',
      // Flat on the wire, the way the multipart path has to spell it: the route
      // reads both fields from the same place whichever way the message came in.
      body: JSON.stringify({
        body,
        clientId,
        replyToId: replyTo?.id ?? null,
        replyQuote: replyTo?.quote ?? null,
        ai,
      }),
    }),

  /**
   * Copies a message into another chat.
   *
   * The same route every other send goes through, and for the same two reasons:
   * one door writes messages, and `clientId` makes a retry produce one of them
   * rather than two. What is different is that the request names a message
   * instead of describing one — the words, the file and the album's tiles are
   * already on the server, so nothing is uploaded and nothing is re-encoded.
   */
  forwardMessage: (chatId: string, messageId: string, clientId: string) =>
    request<MessageDto>(`/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ forwardOf: messageId, clientId }),
    }),

  /**
   * «Пожаловаться» on something that went wrong.
   *
   * Carries only what the person is looking at. Everything an admin will read
   * beside it — which provider, which model, what the last call was — the
   * server assembles itself, because the admin cannot open somebody else's
   * chat to find out and because none of it is this caller's to assert.
   */
  aiPrefs: () => request<AiPrefs>('/ai/prefs'),

  /** Answers with what was stored, not an acknowledgement: the server is what
   *  decides a shape is readable, so «7:0» comes back already gone. */
  saveAiPrefs: (prefs: AiPrefs) =>
    request<AiPrefs>('/ai/prefs', { method: 'PUT', body: JSON.stringify(prefs) }),

  report: (kind: 'ai', messageId: string | null, note?: string) =>
    request<{ id: string; remaining: number }>('/reports', {
      method: 'POST',
      body: JSON.stringify({ kind, messageId, note }),
    }),

  /**
   * Opens a resumable upload and says where to carry on from.
   *
   * Every attachment in this app goes through one of these, however small it
   * is. A single path is worth more than the round trip a one-part photo saves:
   * one thing to reason about when a send is interrupted, and one thing that
   * has to be right.
   */
  beginUpload: (chatId: string, part: { fileName: string; mime: string; totalBytes: number }) =>
    request<UploadTicket>('/uploads', {
      method: 'POST',
      body: JSON.stringify({ conversationId: chatId, ...part }),
    }),

  /**
   * How much of a session the server holds — asked when a send resumes, and
   * never otherwise.
   *
   * A session it has never heard of, or has swept, answers `null` rather than
   * throwing: that is the ordinary ending for a send abandoned for a day, and
   * the caller's answer to it is to open a new one, not to show an error.
   */
  uploadStatus: async (id: string): Promise<UploadStatus | null> => {
    try {
      return await request<UploadStatus>(`/uploads/${id}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /**
   * One slice of a file, at the offset this device believes it is writing to.
   *
   * A 409 is not an error here and is not thrown as one. It means the server is
   * further along than this device thought — a chunk whose response was lost
   * still landed — and the offset it answers with is exactly what is needed to
   * carry on. Sending the slice again anyway would write those bytes twice.
   */
  sendChunk: async (
    id: string,
    offset: number,
    chunk: Blob,
    signal?: AbortSignal,
  ): Promise<ChunkResult> => {
    const path = `/uploads/${id}?offset=${offset}`;
    let res: Response;
    try {
      res = await fetch(apiUrl(path), {
        method: 'PATCH',
        credentials: credentialsMode(),
        headers: { 'Content-Type': 'application/octet-stream', ...authHeaders() },
        body: chunk,
        // Aborted when the send is handed to the browser's own background
        // uploader: two writers on one session would race for the same offset.
        signal,
      });
    } catch {
      // Not reported: an interrupted chunk is the normal working condition of
      // this endpoint — it is what the whole mechanism exists to survive — and
      // filing every one would drown the journal in the app working correctly.
      throw new ApiError('Нет соединения', 0);
    }

    const body = (await res.json().catch(() => null)) as
      | { offset?: number; complete?: boolean; error?: string }
      | null;

    if (res.status === 409) return { ok: false, offset: body?.offset ?? 0 };
    if (!res.ok) {
      if (worthReporting(res.status)) {
        reportClientError({
          kind: 'upload',
          detail: `/uploads/${id}`,
          message: body?.error ?? res.statusText ?? 'chunk failed',
          status: res.status,
        });
      }
      throw new ApiError(body?.error ?? 'Что-то пошло не так', res.status);
    }
    return { ok: true, offset: body?.offset ?? 0, complete: body?.complete === true };
  },

  /**
   * Turns finished uploads into the message that carries them.
   *
   * Idempotent by `clientId`: posting the same one twice hands back the message
   * the first attempt made rather than making a second. That is what lets the
   * outbox retry a finalize it never heard the answer to, which is the failure
   * a phone hits most — every byte uploaded, and the reply lost.
   */
  postMessage: (chatId: string, payload: OutgoingMessage) =>
    request<MessageDto>(`/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** «Просмотрено» for a кружочек: the recipient pressed play on it. */
  markViewed: (chatId: string, messageId: string) =>
    request<void>(`/chats/${chatId}/messages/${messageId}/viewed`, { method: 'POST' }),

  /**
   * «Расшифровать» — have the server turn a voice message into text.
   *
   * Answers 202 with the state it is now in, normally `pending`; the text itself
   * arrives over the socket. Asking for one that is already recognised is not an
   * error and costs nothing — it hands back what is stored — so the button never
   * has to reason about whether somebody else got there first.
   */
  transcribe: (chatId: string, messageId: string) =>
    request<TranscriptView>(`/chats/${chatId}/messages/${messageId}/transcript`, {
      method: 'POST',
    }),

  editMessage: (chatId: string, messageId: string, body: string) =>
    request<MessageDto>(`/chats/${chatId}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),

  /** Neither scope removes anything: the server marks the message instead. */
  deleteMessage: (chatId: string, messageId: string, scope: DeleteScope) =>
    request<void>(`/chats/${chatId}/messages/${messageId}?scope=${scope}`, { method: 'DELETE' }),

  /** `null` takes this person's reaction back off the message. */
  setReaction: (chatId: string, messageId: string, slug: string | null) =>
    request<ReactionView[]>(`/chats/${chatId}/messages/${messageId}/reaction`, {
      method: 'PUT',
      body: JSON.stringify({ slug }),
    }),

  /** The VAPID public key is fetched rather than bundled, so no key is baked into the build. */
  pushConfig: () => request<PushConfig>('/push/config'),

  /** Upsert by endpoint, so this is both "subscribe" and "re-sync on launch". */
  subscribePush: (subscription: PushSubscriptionJSON) =>
    request<{ ok: true }>('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  /**
   * The same row, reached the other way: a native app's device token stands in
   * for an endpoint.
   *
   * The transport is stated rather than inferred, because it cannot be inferred:
   * an APNs token and an FCM one are both opaque strings, and the phone is the
   * only party that knows which registry minted the one it is holding.
   */
  subscribeNative: (transport: NativeTransport, token: string, voipToken?: string | null) =>
    request<{ ok: true }>('/push/subscribe', {
      method: 'POST',
      /**
       * One POST for both tokens, so «this device can be rung properly» lands
       * on one row. On iOS the VoIP field is always stated — a token, or an
       * explicit null — and that null is load-bearing: a build that no longer
       * answers PushKit must get its token cleared, or the server goes on
       * sending VoIP pushes that iOS kills the app for not reporting
       * (`FRONTBOARD 0xbaadca11`, measured twice on a phone). Android sends
       * nothing at all, and an absent field still means «keep what you have»,
       * which is what protects a phone registered by a newer bundle.
       */
      body: JSON.stringify({
        transport,
        token,
        ...(transport === 'apns' ? { voipToken: voipToken ?? null } : {}),
      }),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ ok: true }>('/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),

  /**
   * The notification really appeared. Only the service worker used to say this;
   * inside the app there is none, so the app says it when it next runs — see
   * the drain in chat/ChatContext.tsx and the note on `acked` in push.ts about
   * what that makes the number mean.
   */
  ackPush: (id: string) =>
    request<void>('/push/ack', { method: 'POST', body: JSON.stringify({ id }) }),

  /**
   * The name everyone else reads. Answers with the whole identity, which is
   * what AuthContext holds — nothing else has to be refetched after a rename.
   */
  saveProfile: (displayName: string) =>
    request<Me>('/profile', { method: 'PUT', body: JSON.stringify({ displayName }) }),

  profilePhotos: () => request<ProfilePhoto[]>('/profile/photos'),

  /**
   * Ordinary multipart rather than the resumable machinery a message uses: a
   * session there is addressed to a conversation, and a profile has none.
   */
  addProfilePhoto: (file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return request<{ photo: ProfilePhoto; avatarPhotoId: string | null }>('/profile/photos', {
      method: 'POST',
      body: form,
    });
  },

  setAvatar: (photoId: string) =>
    request<{ avatarPhotoId: string | null }>('/profile/avatar', {
      method: 'PUT',
      body: JSON.stringify({ photoId }),
    }),

  pushSettings: () => request<PushSettings>('/push/settings'),

  savePushSettings: (showPreview: boolean) =>
    request<PushSettings>('/push/settings', {
      method: 'PUT',
      body: JSON.stringify({ showPreview }),
    }),

  testPush: () => request<{ outcome: string }>('/push/test', { method: 'POST' }),

  adminOverview: () => request<AdminOverview>('/admin/overview'),

  /**
   * Walks the disk, so it is fetched when its tab opens rather than on the
   * overview's three-second poll. See the route for why that distinction is
   * load-bearing rather than a preference.
   */
  adminGallery: () => request<AdminGalleryStats>('/admin/gallery'),

  /**
   * The last thirty lines one phone posted — what is behind the single line the
   * table shows. Cheap, unlike the stats above: two indexed reads and no disk.
   */
  adminGalleryLog: (userId: string) =>
    request<{ username: string; lines: AdminGallerySyncLine[] }>(
      `/admin/gallery/log?userId=${encodeURIComponent(userId)}`,
    ),

  /**
   * What arrived when, in buckets, for one account or for everybody.
   *
   * The cheap half of the tab and the reason it is not folded into the stats
   * above: this is re-asked every time the picker changes a name or a preset,
   * and each of those must not cost a walk of two hundred thousand files.
   *
   * The timezone travels with the request because the day the chart is drawn in
   * is the reader's — the server runs on UTC and is read from Moscow, which
   * would otherwise file every evening's uploads under the day before.
   */
  adminGalleryTimeline: (
    userId: string | null,
    range: AdminGalleryRange,
    tzOffset = -new Date().getTimezoneOffset(),
  ) =>
    request<AdminGalleryTimeline>(
      `/admin/gallery/timeline?range=${range}&tz=${tzOffset}` +
        (userId === null ? '' : `&userId=${encodeURIComponent(userId)}`),
    ),

  /**
   * Deletes the stored files and keeps the rows, so nothing syncs again — the
   * row is what answers the phone's «уже здесь». Originals still arriving are
   * left alone; their partial file is somebody's resume offset.
   */
  purgeGallery: (target: { userId: string } | { all: true }) =>
    request<AdminGalleryPurge>('/admin/gallery/purge', {
      method: 'POST',
      body: JSON.stringify(target),
    }),

  /**
   * Creates an account — the same thing the seed script does on the host, minus
   * the shell. A taken nickname comes back as 409 unless `overwrite` says to
   * take it over, which resets that person's password and revokes their sessions.
   */
  createUser: (account: NewAccount) =>
    request<{ created: boolean; account: AdminAccount }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(account),
    }),

  /** Fires in 30s, so the phone can be locked and left to settle into Doze first. */
  testPushDelayed: () =>
    request<{ ok: true; delaySeconds: number }>('/admin/push/test-delayed', { method: 'POST' }),

  /** Applies to the next upload; nothing already sent is re-encoded. */
  saveImageSettings: (patch: ImageSettingsPatch) =>
    request<ImageTuning>('/admin/settings/images', { method: 'PUT', body: JSON.stringify(patch) }),

  resetImageSettings: () =>
    request<ImageTuning>('/admin/settings/images/reset', { method: 'POST' }),

  /**
   * Applies to the next take a device starts, and only after that device has
   * reloaded: the value is encoded into the recording as it is captured, so it
   * travels with the session rather than with the message.
   */
  saveRecordingSettings: (patch: RecordingSettingsPatch) =>
    request<RecordingTuning>('/admin/settings/recording', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetRecordingSettings: () =>
    request<RecordingTuning>('/admin/settings/recording/reset', { method: 'POST' }),

  /**
   * Applies to the next video a device picks, after that device has reloaded —
   * the same contract as the recording settings, because it is the same
   * arrangement: the encoder is in the browser.
   */
  saveVideoImportSettings: (patch: VideoImportSettingsPatch) =>
    request<VideoImportTuning>('/admin/settings/video-import', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetVideoImportSettings: () =>
    request<VideoImportTuning>('/admin/settings/video-import/reset', { method: 'POST' }),

  /**
   * The calls panel, read on its own rather than out of the overview: the tab
   * it lives on is mostly a table of calls, which the overview has no business
   * carrying. Applies to the next call on every device — the numbers ride the
   * ICE credentials, which every call fetches.
   */
  callSettings: () => request<CallSettings>('/admin/settings/calls'),

  saveCallSettings: (patch: CallSettingsPatch) =>
    request<CallSettings>('/admin/settings/calls', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetCallSettings: () => request<CallSettings>('/admin/settings/calls/reset', { method: 'POST' }),

  /** The calls list, newest first. `before` pages by the previous page's oldest. */
  adminCalls: (before?: number) =>
    request<{ calls: AdminCallRow[] }>(`/admin/calls${before ? `?before=${before}` : ''}`),

  adminCall: (id: string) => request<AdminCallDetail>(`/admin/calls/${id}`),

  adminCallsSummary: (days = 7) => request<AdminCallSummary>(`/admin/calls/summary?days=${days}`),

  /**
   * Applies from each phone's next sync run — the engine asks for the targets
   * at the top of every pass. Previews already uploaded are not remade.
   */
  /** The panel's own fetch — the «Галерея» tab loads it on open, off the overview poll. */
  galleryPreviewSettings: () => request<GalleryPreviewTuning>('/admin/settings/gallery-preview'),

  saveGalleryPreviewSettings: (patch: GalleryPreviewSettingsPatch) =>
    request<GalleryPreviewTuning>('/admin/settings/gallery-preview', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetGalleryPreviewSettings: () =>
    request<GalleryPreviewTuning>('/admin/settings/gallery-preview/reset', { method: 'POST' }),

  /** Applies to the next voice message; nothing already sent is recognised again. */
  saveTranscriptionSettings: (patch: TranscriptionSettingsPatch) =>
    request<TranscriptionTuning>('/admin/settings/transcription', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetTranscriptionSettings: () =>
    request<TranscriptionTuning>('/admin/settings/transcription/reset', { method: 'POST' }),

  /** Applies to the assistant's next reply; nothing already said changes. */
  /** «Разобрано». The row keeps its place with a date on it; nothing is removed. */
  resolveReport: (id: string) =>
    request<{ ok: true }>(`/admin/reports/${id}/resolve`, { method: 'POST' }),

  saveAiSettings: (patch: AiSettingsPatch) =>
    request<AiTuning>('/admin/settings/ai', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  resetAiSettings: () => request<AiTuning>('/admin/settings/ai/reset', { method: 'POST' }),

  /** Every override, spelled the way .env spells it — ready to paste. */
  envOverrides: () => request<EnvOverrides>('/admin/settings/env'),

  listIntegrations: () =>
    request<{ integrations: Integration[] }>('/admin/integrations').then((r) => r.integrations),

  /**
   * The token comes back exactly once, here. It is stored as a digest and
   * cannot be read back, so whatever does not get copied out of this response
   * is gone and the integration has to be given a new one.
   */
  createIntegration: (draft: NewIntegration) =>
    request<{ integration: Integration; token: string }>('/admin/integrations', {
      method: 'POST',
      body: JSON.stringify(draft),
    }),

  /** A new token; the old one stops working the moment this resolves. */
  rotateIntegrationToken: (id: string) =>
    request<{ token: string }>(`/admin/integrations/${id}/token`, { method: 'POST' }),

  /** Its name, whether it is on, and the reports grant. Where it delivers is not set from here. */
  updateIntegration: (id: string, patch: { name?: string; disabled?: boolean; serviceRead?: boolean }) =>
    request<{ integration: Integration }>(`/admin/integrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((r) => r.integration),

  /** Marks it gone. The bot account and everything it sent stay. */
  deleteIntegration: (id: string) =>
    request<void>(`/admin/integrations/${id}`, { method: 'DELETE' }),

  /** Every bot — integrations and assistants alike — with its access and folder. */
  listBots: () => request<BotsPayload>('/admin/bots'),

  /**
   * Both of these answer with the whole page again rather than one row: giving
   * a bot access hands out chats, and taking a bot out of a folder can take the
   * folder with it, so what changed is never only the row that was touched.
   */
  saveBotAccess: (botUserId: string, patch: { mode: BotAccessMode; userIds: string[] }) =>
    request<BotsPayload>(`/admin/bots/${botUserId}/access`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  /** Renames it for everybody — every open client is told over the socket. */
  saveBotName: (botUserId: string, name: string) =>
    request<BotsPayload>(`/admin/bots/${botUserId}/name`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    }),

  saveBotGroup: (botUserId: string, group: string | null) =>
    request<BotsPayload>(`/admin/bots/${botUserId}/group`, {
      method: 'PUT',
      body: JSON.stringify({ group }),
    }),
};

/**
 * Attachments are addressed by message id; the server checks participation.
 *
 * `variant: 'thumb'` asks for the small copy that image bubbles and album tiles
 * render. The server falls back to the full file when there is no thumbnail — a
 * document, a picture that was already small, anything sent before thumbnails
 * existed — so it can be requested unconditionally for a photo. A video without
 * a poster is the one case it must not be (see AttachmentView.hasPreview).
 *
 * `index` picks one tile out of an album, which is the only kind whose bytes are
 * not on the message row itself.
 *
 * `download` asks the server for `attachment` on a file that would render
 * inline, so the viewer's «Скачать» ends in a saved file rather than trusting
 * the anchor's `download` attribute, which an installed iOS PWA half-supports.
 *
 * On a native build the origin is spelled out and a media ticket is appended,
 * an element's `src` having no way to carry the bearer header the rest of the
 * API is authenticated with. On the web both are empty and this is the same
 * relative path it has always produced.
 */
export const fileUrl = (
  messageId: string,
  options: { variant?: 'thumb'; index?: number; download?: boolean } = {},
) => {
  const query = new URLSearchParams();
  if (options.variant) query.set('variant', options.variant);
  if (options.index !== undefined) query.set('index', String(options.index));
  if (options.download) query.set('download', '1');
  return `${apiOrigin}/api/files/${messageId}${withTicket(query.toString())}`;
};

/** The query as it goes on the end of a URL, with the media ticket in it. */
function withTicket(query: string): string {
  const full = [query, mediaQuery()].filter(Boolean).join('&');
  return full ? `?${full}` : '';
}

/**
 * A profile photograph, by its id. Its own route rather than a variant of the
 * one above because who may look is a different question — see
 * photoVisibleTo in server/src/profile.ts.
 *
 * Every avatar asks for `thumb`: a 42px square has no use for the full picture,
 * and a photo too small to have needed a thumbnail comes back whole anyway.
 */
export const photoUrl = (photoId: string, options: { variant?: 'thumb' } = {}) =>
  `${apiOrigin}/api/photos/${photoId}${withTicket(options.variant ? `variant=${options.variant}` : '')}`;

/**
 * The published Android build, from the manifest committed beside the APK.
 *
 * Not `/api` and not through `request` above: this is the one thing the app
 * asks for that must work without a session — it is served by the same
 * unauthenticated mount that hands out the APK itself (server/src/index.ts),
 * and it is asked for on launch, possibly before anyone has signed in. It also
 * must not be reported as an API error when it fails; an update check that
 * could not reach the server is not an incident, it is a phone on a train.
 *
 * `no-store` because a cached answer is the exact failure this exists to
 * prevent: the whole point is learning that something changed.
 */
export async function fetchAndroidRelease(): Promise<unknown> {
  const res = await fetch(`${apiOrigin}/download/android.json`, {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!res.ok) throw new ApiError('нет манифеста', res.status);
  return (await res.json()) as unknown;
}

/** Where that build's APK is. Relative to the manifest, so one place names it. */
export const androidApkUrl = (file: string) => `${apiOrigin}/download/${file}`;
